/**
 * Reports API service - handles report generation, export, and scheduling
 */

import { apiClient } from './client';

// ============================================================================
// Types
// ============================================================================

export type ReportType =
  | 'project_status'
  | 'sprint_summary'
  | 'team_productivity'
  | 'overdue_tasks'
  | 'weekly_digest'
  | 'monthly_summary';

export interface ReportTypeInfo {
  id: ReportType;
  name: string;
  description: string;
}

export interface TaskMetrics {
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  overdueTasks: number;
  createdInPeriod: number;
  completedInPeriod: number;
  completionRate: number;
  tasksByPriority: Record<string, number>;
  tasksByType: Record<string, number>;
  tasksByColumn: Record<string, number>;
}

export interface ReportProjectMetrics {
  projectId: string;
  projectName: string;
  projectKey: string;
  boardCount: number;
  memberCount: number;
  healthScore: number;
  taskMetrics: TaskMetrics;
}

export interface TeamMetrics {
  memberId: string;
  memberName: string;
  memberEmail: string;
  tasksAssigned: number;
  tasksCompleted: number;
  tasksOverdue: number;
  productivityScore: number;
}

export interface OverdueTask {
  id: string;
  taskKey: string;
  title: string;
  priority: string;
  dueDate?: string | undefined;
  daysOverdue: number;
  assigneeId?: string | undefined;
  projectId: string;
  projectName: string;
}

export interface UpcomingDeadline {
  id: string;
  taskKey: string;
  title: string;
  priority: string;
  dueDate?: string | undefined;
  daysUntilDue: number;
  assigneeId?: string | undefined;
  projectId: string;
  projectName: string;
}

export interface RecentCompletion {
  id: string;
  taskKey: string;
  title: string;
  priority: string;
  completedAt?: string | undefined;
  assigneeId?: string | undefined;
  projectId: string;
  projectName: string;
}

export interface AISummary {
  executiveSummary: string;
  keyInsights: string[];
  actionItems: string[];
  riskAssessment: string;
  outlook: string;
}

export interface Report {
  reportType: ReportType;
  generatedAt: Date;
  periodStart: Date;
  periodEnd: Date;
  organizationId?: string | undefined;
  projectMetrics: ReportProjectMetrics[];
  teamMetrics: TeamMetrics[];
  overdueTasks: OverdueTask[];
  upcomingDeadlines: UpcomingDeadline[];
  recentCompletions: RecentCompletion[];
  highlights: string[];
  concerns: string[];
  recommendations: string[];
  aiSummary?: AISummary | undefined;
}

export interface GenerateReportRequest {
  reportType: ReportType;
  organizationId: string;
  projectId?: string | undefined;
  boardId?: string | undefined;
  startDate?: Date | undefined;
  endDate?: Date | undefined;
  includeAiSummary?: boolean | undefined;
  assigneeIds?: string[] | undefined;
}

export interface ScheduleReportRequest {
  reportType: ReportType;
  organizationId: string;
  projectIds?: string[] | undefined;
  frequency: 'daily' | 'weekly' | 'monthly';
  recipientEmails: string[];
  includeAiSummary?: boolean | undefined;
  dayOfWeek?: number | undefined; // 0 = Monday
  dayOfMonth?: number | undefined; // 1-31
  hour?: number | undefined; // 0-23 UTC
  enabled?: boolean | undefined;
}

export interface ScheduleReportResponse {
  scheduleId: string;
  message: string;
  nextRun?: Date | undefined;
}

// ============================================================================
// API Response Types (snake_case from backend)
// ============================================================================

interface TaskMetricsApi {
  total_tasks: number;
  completed_tasks: number;
  in_progress_tasks: number;
  overdue_tasks: number;
  created_in_period: number;
  completed_in_period: number;
  completion_rate: number;
  tasks_by_priority: Record<string, number>;
  tasks_by_type: Record<string, number>;
  tasks_by_column: Record<string, number>;
}

interface ProjectMetricsApi {
  project_id: string;
  project_name: string;
  project_key: string;
  board_count: number;
  member_count: number;
  health_score: number;
  task_metrics: TaskMetricsApi;
}

interface TeamMetricsApi {
  member_id: string;
  member_name: string;
  member_email: string;
  tasks_assigned: number;
  tasks_completed: number;
  tasks_overdue: number;
  productivity_score: number;
}

interface OverdueTaskApi {
  id: string;
  task_key: string;
  title: string;
  priority: string;
  due_date?: string;
  days_overdue: number;
  assignee_id?: string;
  project_id: string;
  project_name: string;
}

interface UpcomingDeadlineApi {
  id: string;
  task_key: string;
  title: string;
  priority: string;
  due_date?: string;
  days_until_due: number;
  assignee_id?: string;
  project_id: string;
  project_name: string;
}

interface RecentCompletionApi {
  id: string;
  task_key: string;
  title: string;
  priority: string;
  completed_at?: string;
  assignee_id?: string;
  project_id: string;
  project_name: string;
}

interface AISummaryApi {
  executive_summary: string;
  key_insights: string[];
  action_items: string[];
  risk_assessment: string;
  outlook: string;
}

interface ReportApi {
  report_type: string;
  generated_at: string;
  period_start: string;
  period_end: string;
  organization_id?: string;
  project_metrics: ProjectMetricsApi[];
  team_metrics: TeamMetricsApi[];
  overdue_tasks: OverdueTaskApi[];
  upcoming_deadlines: UpcomingDeadlineApi[];
  recent_completions: RecentCompletionApi[];
  highlights: string[];
  concerns: string[];
  recommendations: string[];
  ai_summary?: AISummaryApi;
}

interface ScheduleReportResponseApi {
  schedule_id: string;
  message: string;
  next_run?: string;
}

// ============================================================================
// Transformers
// Note: Backend uses alias_generator=to_camel, so API responses are camelCase
// ============================================================================

function transformTaskMetrics(api: any): TaskMetrics {
  return {
    totalTasks: api.totalTasks ?? api.total_tasks,
    completedTasks: api.completedTasks ?? api.completed_tasks,
    inProgressTasks: api.inProgressTasks ?? api.in_progress_tasks,
    overdueTasks: api.overdueTasks ?? api.overdue_tasks,
    createdInPeriod: api.createdInPeriod ?? api.created_in_period,
    completedInPeriod: api.completedInPeriod ?? api.completed_in_period,
    completionRate: api.completionRate ?? api.completion_rate,
    tasksByPriority: api.tasksByPriority ?? api.tasks_by_priority,
    tasksByType: api.tasksByType ?? api.tasks_by_type,
    tasksByColumn: api.tasksByColumn ?? api.tasks_by_column,
  };
}

function transformProjectMetrics(api: any): ReportProjectMetrics {
  return {
    projectId: api.projectId ?? api.project_id,
    projectName: api.projectName ?? api.project_name,
    projectKey: api.projectKey ?? api.project_key,
    boardCount: api.boardCount ?? api.board_count,
    memberCount: api.memberCount ?? api.member_count,
    healthScore: api.healthScore ?? api.health_score,
    taskMetrics: transformTaskMetrics(api.taskMetrics ?? api.task_metrics),
  };
}

function transformTeamMetrics(api: any): TeamMetrics {
  return {
    memberId: api.memberId ?? api.member_id,
    memberName: api.memberName ?? api.member_name,
    memberEmail: api.memberEmail ?? api.member_email,
    tasksAssigned: api.tasksAssigned ?? api.tasks_assigned,
    tasksCompleted: api.tasksCompleted ?? api.tasks_completed,
    tasksOverdue: api.tasksOverdue ?? api.tasks_overdue,
    productivityScore: api.productivityScore ?? api.productivity_score,
  };
}

function transformOverdueTask(api: any): OverdueTask {
  return {
    id: api.id,
    taskKey: api.taskKey ?? api.task_key,
    title: api.title,
    priority: api.priority,
    dueDate: api.dueDate ?? api.due_date,
    daysOverdue: api.daysOverdue ?? api.days_overdue,
    assigneeId: api.assigneeId ?? api.assignee_id,
    projectId: api.projectId ?? api.project_id,
    projectName: api.projectName ?? api.project_name,
  };
}

function transformUpcomingDeadline(api: any): UpcomingDeadline {
  return {
    id: api.id,
    taskKey: api.taskKey ?? api.task_key,
    title: api.title,
    priority: api.priority,
    dueDate: api.dueDate ?? api.due_date,
    daysUntilDue: api.daysUntilDue ?? api.days_until_due,
    assigneeId: api.assigneeId ?? api.assignee_id,
    projectId: api.projectId ?? api.project_id,
    projectName: api.projectName ?? api.project_name,
  };
}

function transformRecentCompletion(api: any): RecentCompletion {
  return {
    id: api.id,
    taskKey: api.taskKey ?? api.task_key,
    title: api.title,
    priority: api.priority,
    completedAt: api.completedAt ?? api.completed_at,
    assigneeId: api.assigneeId ?? api.assignee_id,
    projectId: api.projectId ?? api.project_id,
    projectName: api.projectName ?? api.project_name,
  };
}

function transformAISummary(api: any): AISummary {
  return {
    executiveSummary: api.executiveSummary ?? api.executive_summary,
    keyInsights: api.keyInsights ?? api.key_insights,
    actionItems: api.actionItems ?? api.action_items,
    riskAssessment: api.riskAssessment ?? api.risk_assessment,
    outlook: api.outlook,
  };
}

function transformReport(api: any): Report {
  const reportType = api.reportType ?? api.report_type;
  const generatedAt = api.generatedAt ?? api.generated_at;
  const periodStart = api.periodStart ?? api.period_start;
  const periodEnd = api.periodEnd ?? api.period_end;
  const projectMetrics = api.projectMetrics ?? api.project_metrics ?? [];
  const teamMetrics = api.teamMetrics ?? api.team_metrics ?? [];
  const overdueTasks = api.overdueTasks ?? api.overdue_tasks ?? [];
  const upcomingDeadlines = api.upcomingDeadlines ?? api.upcoming_deadlines ?? [];
  const recentCompletions = api.recentCompletions ?? api.recent_completions ?? [];
  const aiSummary = api.aiSummary ?? api.ai_summary;

  return {
    reportType: reportType as ReportType,
    generatedAt: new Date(generatedAt),
    periodStart: new Date(periodStart),
    periodEnd: new Date(periodEnd),
    organizationId: api.organizationId ?? api.organization_id,
    projectMetrics: projectMetrics.map(transformProjectMetrics),
    teamMetrics: teamMetrics.map(transformTeamMetrics),
    overdueTasks: overdueTasks.map(transformOverdueTask),
    upcomingDeadlines: upcomingDeadlines.map(transformUpcomingDeadline),
    recentCompletions: recentCompletions.map(transformRecentCompletion),
    highlights: api.highlights ?? [],
    concerns: api.concerns ?? [],
    recommendations: api.recommendations ?? [],
    aiSummary: aiSummary ? transformAISummary(aiSummary) : undefined,
  };
}

// ============================================================================
// API Methods
// ============================================================================

/**
 * Reports API methods
 */
export const reportsApi = {
  /**
   * Generate a report on demand
   */
  async generateReport(request: GenerateReportRequest): Promise<Report> {
    const response = await apiClient.post<ReportApi>('/reports', {
      report_type: request.reportType,
      organization_id: request.organizationId,
      project_id: request.projectId,
      board_id: request.boardId,
      start_date: request.startDate?.toISOString(),
      end_date: request.endDate?.toISOString(),
      include_ai_summary: request.includeAiSummary ?? true,
      assignee_ids: request.assigneeIds,
    });

    return transformReport(response.data);
  },

  /**
   * Export report as PDF
   * Returns a blob URL for downloading
   */
  async exportReportPdf(request: GenerateReportRequest): Promise<Blob> {
    const response = await apiClient.post<Blob>(
      '/reports/export/pdf',
      {
        report_type: request.reportType,
        organization_id: request.organizationId,
        project_id: request.projectId,
        board_id: request.boardId,
        start_date: request.startDate?.toISOString(),
        end_date: request.endDate?.toISOString(),
        include_ai_summary: request.includeAiSummary ?? true,
        assignee_ids: request.assigneeIds,
      },
      {
        responseType: 'blob',
      }
    );

    return response.data;
  },

  /**
   * Get available report types
   */
  async getReportTypes(): Promise<ReportTypeInfo[]> {
    const response = await apiClient.get<
      Array<{
        id: string;
        name: string;
        description: string;
      }>
    >('/reports/types');

    return response.data.map((type) => ({
      id: type.id as ReportType,
      name: type.name,
      description: type.description,
    }));
  },

  /**
   * Get report for a specific project
   */
  async getProjectReport(
    projectId: string,
    options?: {
      reportType?: ReportType;
      startDate?: Date;
      endDate?: Date;
      includeAiSummary?: boolean;
    }
  ): Promise<Report> {
    const response = await apiClient.get<ReportApi>(`/reports/project/${projectId}`, {
      params: {
        report_type: options?.reportType ?? 'project_status',
        start_date: options?.startDate?.toISOString(),
        end_date: options?.endDate?.toISOString(),
        include_ai_summary: options?.includeAiSummary ?? true,
      },
    });

    return transformReport(response.data);
  },

  /**
   * Get list of overdue tasks
   */
  async getOverdueTasks(
    organizationId: string,
    options?: {
      projectId?: string;
      limit?: number;
    }
  ): Promise<OverdueTask[]> {
    const response = await apiClient.get<OverdueTaskApi[]>('/reports/overdue', {
      params: {
        organization_id: organizationId,
        project_id: options?.projectId,
        limit: options?.limit ?? 50,
      },
    });

    return response.data.map(transformOverdueTask);
  },

  /**
   * Get tasks with upcoming deadlines
   */
  async getUpcomingDeadlines(
    organizationId: string,
    options?: {
      projectId?: string;
      daysAhead?: number;
      limit?: number;
    }
  ): Promise<UpcomingDeadline[]> {
    const response = await apiClient.get<UpcomingDeadlineApi[]>('/reports/upcoming-deadlines', {
      params: {
        organization_id: organizationId,
        project_id: options?.projectId,
        days_ahead: options?.daysAhead ?? 7,
        limit: options?.limit ?? 20,
      },
    });

    return response.data.map(transformUpcomingDeadline);
  },

  /**
   * Schedule a recurring report
   */
  async scheduleReport(request: ScheduleReportRequest): Promise<ScheduleReportResponse> {
    const response = await apiClient.post<ScheduleReportResponseApi>('/reports/schedule', {
      report_type: request.reportType,
      organization_id: request.organizationId,
      project_ids: request.projectIds,
      frequency: request.frequency,
      recipient_emails: request.recipientEmails,
      include_ai_summary: request.includeAiSummary ?? true,
      day_of_week: request.dayOfWeek,
      day_of_month: request.dayOfMonth,
      hour: request.hour ?? 9,
      enabled: request.enabled ?? true,
    });

    return {
      scheduleId: response.data.schedule_id,
      message: response.data.message,
      nextRun: response.data.next_run ? new Date(response.data.next_run) : undefined,
    };
  },

  /**
   * Download report PDF as a file
   * Convenience method that creates and clicks a download link
   */
  async downloadReportPdf(
    request: GenerateReportRequest,
    filename?: string
  ): Promise<void> {
    const blob = await this.exportReportPdf(request);

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      filename ||
      `${request.reportType}_report_${new Date().toISOString().slice(0, 10)}.pdf`;

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up blob URL
    window.URL.revokeObjectURL(url);
  },
};

export default reportsApi;
